import React, { useCallback, useEffect, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import { ColumnType } from "antd/es/table/interface";
import Summary from "../components/declarants/summary";
import ImportDeclarants from "../components/declarants/importDeclarants";
import DateRange from "../components/declarants/dateRange";
import CreateDeclarant from "../components/declarants/createDeclarant";
import Search from "../components/declarants/search";
import Download from "../components/declarants/download";
import Settings from "../components/declarants/Settings";
import { DateRangeDate, IObjectMap } from "../interfaces/common";
import COLUMNS from "../components/tables/sigmaTable/config";
import {
  SIGMA_ACTIVE_COLUMNS,
  SIGMA_ORDER_COLUMNS,
} from "../constants/settings";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import CalculateClientSum from "../components/buttons/calculateClientsFeeButton";
import SigmaStore from "../stores/SigmaStore";
import SettingsStore from "../stores/SettingsStore";
import UserStore from "../stores/UserStore";
import CollapseHeader from "../components/CollapseHeader";
import Card from "../components/Card/Card";
import TableWrapper from "../components/declarants/tableWrapper";
import ReordableVirtualTable from "../components/common/Table/ReordableVirtualTable";
import { Declarant } from "../models/Declarant";
import eventEmitter from "../services/EventEmitter";
import RefreshButton from "../components/buttons/RefreshButton";

const sigmaStore = new SigmaStore();
let isLoaded = false;

const onLoadHandler = (
  parameters?: IObjectMap,
  forceUpdate?: boolean
): void => {
  sigmaStore.load(parameters || {}, forceUpdate);
};

const changeHandler = (values: Array<number | string>) => {
  sigmaStore.changeSelectedRows(values);
};

const SigmaPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  useEffect(() => {
    if (isLoaded) {
      eventEmitter.emit("table:re-render", null);
      return;
    }

    sigmaStore.load({}, true);
    isLoaded = true;
  }, []);

  const [height, setHeight] = useState(window.innerHeight);

  const changePositionHandler = useCallback(({ top }) => {
    setHeight(window.innerHeight - top - 72);
  }, []);

  useEffect(() => {
    settingsStore.setColumns(
      updateSortingAndFiltersOfColumns(
        userStore.authorizedOnly(COLUMNS(settingsStore)) ||
          COLUMNS(settingsStore),
        sigmaStore.parameters as IObjectMap
      ),
      SIGMA_ORDER_COLUMNS,
      SIGMA_ACTIVE_COLUMNS
    );
  }, [
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    sigmaStore.parameters,
  ]);

  const columns = useMemo(() => {
    if (settingsStore.isEnabledScrollbar) {
      return settingsStore.activeColumns;
    }

    return settingsStore.activeColumns.map((column) => ({
      ...column,
      width: undefined,
    }));
  }, [settingsStore.activeColumns, settingsStore.isEnabledScrollbar]);

  return (
    <>
      <CollapseHeader>
        <DateRange
          store={sigmaStore}
          startDate={sigmaStore.parameters.start_date as DateRangeDate}
          endDate={sigmaStore.parameters.end_date as DateRangeDate}
        />
        <Search store={sigmaStore} />
        <Summary store={sigmaStore} />
      </CollapseHeader>
      <Card>
        <Card.Header>
          <ImportDeclarants store={sigmaStore} />
          <Download store={sigmaStore} />
          <CreateDeclarant
            clients={settingsStore.clients || []}
            noClient={settingsStore.noClient}
            departments={settingsStore.departments || []}
          />
          <CalculateClientSum />
          <RefreshButton refresh={sigmaStore} />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper
          className="custom-virtual-table"
          enableScrollBar={settingsStore.isEnabledScrollbar}
          changePositionHandler={changePositionHandler}
        >
          <ReordableVirtualTable
            settingsStore={settingsStore}
            dataSource={sigmaStore.data}
            loading={
              sigmaStore.isLoadingTableData || sigmaStore.isUploadingXmlFile
            }
            scroll={{ y: height, x: "100vw" }}
            columns={columns as ColumnType<Declarant>[]}
            rowClassName={(record: Declarant) => {
              let classes = "";

              if (record.exposed) {
                classes += " exposed-row";
              }

              if (sigmaStore.selectedRows.includes(record.id as number)) {
                classes += " selected-row";
              }

              return classes.trim();
            }}
            selected={sigmaStore.selectedRows}
            changeSelectHandler={changeHandler}
            loadMoreHandler={onLoadHandler}
            hasMoreItems={sigmaStore.hasMoreItems}
          />
        </TableWrapper>
      </Card>
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(SigmaPage));
